import { useState } from 'react';
import {
  DocumentTextIcon,
  LinkIcon,
  VideoCameraIcon,
  SparklesIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import type { ClubTheme } from './themes';
import type { EventArtifacts } from './types';
import { useUpdateArtifacts } from './queries';

interface EventArtifactsPanelProps {
  clubId: string;
  eventId: string;
  artifacts: EventArtifacts;
  canEdit: boolean;
  theme: ClubTheme;
}

export default function EventArtifactsPanel({
  clubId,
  eventId,
  artifacts,
  canEdit,
  theme,
}: EventArtifactsPanelProps) {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(artifacts.notes || '');
  const [recordingUrl, setRecordingUrl] = useState(artifacts.recording_url || '');
  const [newLink, setNewLink] = useState('');
  const [newLinkLabel, setNewLinkLabel] = useState('');

  const updateArtifacts = useUpdateArtifacts(clubId, eventId);

  const hasArtifacts =
    artifacts.notes || artifacts.recording_url || artifacts.ai_summary || artifacts.links?.length;

  const handleSaveNotes = () => {
    updateArtifacts.mutate({ notes }, { onSuccess: () => setEditing(false) });
  };

  const handleSaveRecording = () => {
    updateArtifacts.mutate({ recording_url: recordingUrl });
  };

  const handleAddLink = () => {
    if (!newLink.trim()) return;
    const links = [...(artifacts.links || []), { url: newLink.trim(), label: newLinkLabel.trim() || undefined }];
    updateArtifacts.mutate({ links }, {
      onSuccess: () => {
        setNewLink('');
        setNewLinkLabel('');
      },
    });
  };

  const handleRemoveLink = (idx: number) => {
    const links = (artifacts.links || []).filter((_, i) => i !== idx);
    updateArtifacts.mutate({ links });
  };

  if (!hasArtifacts && !canEdit) return null;

  const inputStyle = {
    backgroundColor: `${theme.colors.muted}10`,
    color: theme.colors.text,
    borderColor: `${theme.colors.muted}30`,
  };

  return (
    <div
      className="rounded-xl p-3 space-y-3"
      style={{ backgroundColor: `${theme.colors.surface}`, border: `1px solid ${theme.colors.muted}15` }}
    >
      <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: theme.colors.muted }}>
        Event Artifacts
      </h4>

      {/* Notes */}
      {(artifacts.notes || canEdit) && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <DocumentTextIcon className="h-3.5 w-3.5" style={{ color: theme.colors.muted }} />
            <span className="text-[11px] font-medium" style={{ color: theme.colors.muted }}>Notes</span>
            {canEdit && !editing && (
              <button type="button" onClick={() => setEditing(true)}>
                <PencilIcon className="h-3 w-3" style={{ color: theme.colors.primary }} />
              </button>
            )}
          </div>
          {editing ? (
            <div className="space-y-1">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full resize-none rounded-lg border px-2 py-1.5 text-xs outline-none"
                style={inputStyle}
              />
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={handleSaveNotes}
                  disabled={updateArtifacts.isPending}
                  className="rounded px-2 py-1 text-[10px] font-semibold"
                  style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setNotes(artifacts.notes || ''); }}
                  className="rounded px-2 py-1 text-[10px]"
                  style={{ color: theme.colors.muted }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : artifacts.notes ? (
            <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: theme.colors.text }}>
              {artifacts.notes}
            </p>
          ) : null}
        </div>
      )}

      {/* AI Summary */}
      {artifacts.ai_summary && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <SparklesIcon className="h-3.5 w-3.5" style={{ color: theme.colors.primary }} />
            <span className="text-[11px] font-medium" style={{ color: theme.colors.muted }}>AI Summary</span>
          </div>
          <p className="text-xs leading-relaxed rounded-lg p-2" style={{ backgroundColor: `${theme.colors.primary}08`, color: theme.colors.text }}>
            {artifacts.ai_summary}
          </p>
        </div>
      )}

      {/* Recording */}
      {(artifacts.recording_url || canEdit) && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <VideoCameraIcon className="h-3.5 w-3.5" style={{ color: theme.colors.muted }} />
            <span className="text-[11px] font-medium" style={{ color: theme.colors.muted }}>Recording</span>
          </div>
          {artifacts.recording_url ? (
            <a
              href={artifacts.recording_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs underline"
              style={{ color: theme.colors.primary }}
            >
              Watch recording
            </a>
          ) : canEdit ? (
            <div className="flex gap-1">
              <input
                type="url"
                placeholder="Paste recording URL"
                value={recordingUrl}
                onChange={(e) => setRecordingUrl(e.target.value)}
                className="flex-1 rounded-lg border px-2 py-1 text-xs outline-none"
                style={inputStyle}
              />
              <button
                type="button"
                onClick={handleSaveRecording}
                disabled={!recordingUrl.trim() || updateArtifacts.isPending}
                className="rounded px-2 py-1 text-[10px] font-semibold disabled:opacity-40"
                style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
              >
                Add
              </button>
            </div>
          ) : null}
        </div>
      )}

      {/* Shared Links */}
      {((artifacts.links && artifacts.links.length > 0) || canEdit) && (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <LinkIcon className="h-3.5 w-3.5" style={{ color: theme.colors.muted }} />
            <span className="text-[11px] font-medium" style={{ color: theme.colors.muted }}>Resources</span>
          </div>
          <ul className="space-y-0.5">
            {(artifacts.links || []).map((link, idx) => (
              <li key={idx} className="flex items-center gap-1">
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs underline truncate"
                  style={{ color: theme.colors.primary }}
                >
                  {link.label || link.url}
                </a>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleRemoveLink(idx)}
                    className="shrink-0 text-[10px]"
                    style={{ color: theme.colors.muted }}
                  >
                    x
                  </button>
                )}
              </li>
            ))}
          </ul>
          {canEdit && (
            <div className="flex gap-1">
              <input
                type="text"
                placeholder="Label (optional)"
                value={newLinkLabel}
                onChange={(e) => setNewLinkLabel(e.target.value)}
                className="w-24 rounded-lg border px-2 py-1 text-xs outline-none"
                style={inputStyle}
              />
              <input
                type="url"
                placeholder="URL"
                value={newLink}
                onChange={(e) => setNewLink(e.target.value)}
                className="flex-1 rounded-lg border px-2 py-1 text-xs outline-none"
                style={inputStyle}
              />
              <button
                type="button"
                onClick={handleAddLink}
                disabled={!newLink.trim() || updateArtifacts.isPending}
                className="rounded px-2 py-1 text-[10px] font-semibold disabled:opacity-40"
                style={{ backgroundColor: theme.colors.primary, color: theme.colors.bg }}
              >
                Add
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
